import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import type { UserDocument } from '@/types/firebase.types'

interface UseUserProfileResult {
  profile: UserDocument | null
  loading: boolean
  error: string | null
  reload: () => void
}

export function useUserProfile(): UseUserProfileResult {
  const { user, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState<UserDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const ref = typedDoc<UserDocument>('users', user.uid)
    getDocument(ref)
      .then((doc) => setProfile(doc ?? null))
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load profile'))
      )
      .finally(() => setLoading(false))
  }, [user, authLoading, tick])

  return { profile, loading, error, reload: () => setTick((t) => t + 1) }
}
